import { useCallback, useEffect, useState } from "react"
import { HTTPError } from "ky"
import { useTranslation } from "react-i18next"

import { Button, Input, Label } from "@f2e/ui/components/base"

import { useToast } from "@/hooks"
import {
  FetchStatus,
  useGetWorkspacePaymentGatewayApi,
  useUpsertWorkspacePaymentGatewayApi,
} from "@/hooks/api"
import { hasPermission } from "@/lib/utils"
import { Permission, Workspace } from "@/types"

export interface WorkspacePaymentGatewaySectionProps {
  workspace: Workspace
}

const WorkspacePaymentGatewaySection = ({
  workspace,
}: WorkspacePaymentGatewaySectionProps) => {
  const { t } = useTranslation()
  const { alertToast, successToast } = useToast()
  const [merchantId, setMerchantId] = useState("")
  const [hashKey, setHashKey] = useState("")
  const [hashIv, setHashIv] = useState("")
  const [hashKeyPreview, setHashKeyPreview] = useState("")
  const [hashIvPreview, setHashIvPreview] = useState("")
  const [hasExistingAccount, setHasExistingAccount] = useState(false)

  const {
    data: gatewayResp,
    error: gatewayError,
    fetchFn: fetchGatewayAccount,
    status: gatewayStatus,
  } = useGetWorkspacePaymentGatewayApi(workspace.id)
  const {
    data: upsertResp,
    error: upsertError,
    fetchFn: upsertGatewayAccount,
    status: upsertStatus,
  } = useUpsertWorkspacePaymentGatewayApi(workspace.id)

  const canEdit = hasPermission(
    workspace.myPermissions,
    Permission.WsUpdateResources
  )

  useEffect(() => {
    fetchGatewayAccount()
  }, [fetchGatewayAccount])

  useEffect(() => {
    const account = gatewayResp?.data
    if (!account) return
    setHasExistingAccount(true)
    setMerchantId(account.merchant_id)
    setHashKeyPreview(account.hash_key_preview || "")
    setHashIvPreview(account.hash_iv_preview || "")
    setHashKey("")
    setHashIv("")
  }, [gatewayResp])

  useEffect(() => {
    if (!gatewayError) return
    if (
      gatewayError instanceof HTTPError &&
      gatewayError.response.status === 404
    ) {
      setHasExistingAccount(false)
      setMerchantId("")
      setHashKeyPreview("")
      setHashIvPreview("")
      setHashKey("")
      setHashIv("")
      return
    }
    alertToast({
      title: t("settings.payments.errors.loadFailedTitle"),
      description: t("settings.payments.errors.loadFailedDescription"),
    })
  }, [gatewayError, alertToast, t])

  useEffect(() => {
    if (!upsertResp?.data) return
    successToast({
      title: t("settings.payments.savedTitle"),
      description: t("settings.payments.savedDescription"),
    })
    setHasExistingAccount(true)
    setHashKey("")
    setHashIv("")
    fetchGatewayAccount({ preserveData: true })
  }, [upsertResp, successToast, t, fetchGatewayAccount])

  useEffect(() => {
    if (!upsertError) return
    alertToast({
      title: t("settings.payments.errors.saveFailedTitle"),
      description: t("settings.payments.errors.saveFailedDescription"),
    })
  }, [upsertError, alertToast, t])

  const handleSave = useCallback(() => {
    const trimmedMerchantId = merchantId.trim()
    if (!trimmedMerchantId) {
      alertToast({
        title: t("settings.payments.errors.validationTitle"),
        description: t("settings.payments.errors.merchantIdRequired"),
      })
      return
    }

    if (!hasExistingAccount && (!hashKey.trim() || !hashIv.trim())) {
      alertToast({
        title: t("settings.payments.errors.validationTitle"),
        description: t("settings.payments.errors.hashRequiredForCreate"),
      })
      return
    }

    upsertGatewayAccount({
      jsonBody: {
        merchant_id: trimmedMerchantId,
        hash_key: hashKey.trim() || undefined,
        hash_iv: hashIv.trim() || undefined,
        is_active: true,
      },
    })
  }, [
    alertToast,
    hasExistingAccount,
    hashIv,
    hashKey,
    merchantId,
    t,
    upsertGatewayAccount,
  ])

  return (
    <section className="space-y-4 rounded-lg border p-4">
      <div className="space-y-1">
        <h4 className="text-lg font-semibold">
          {t("workspace.payments.gatewaySetup.title")}
        </h4>
        <p className="text-sm text-muted-foreground">
          {t("workspace.payments.gatewaySetup.description")}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2 md:col-span-2">
          <Label>{t("settings.payments.merchantIdLabel")}</Label>
          <Input
            value={merchantId}
            onChange={(event) => setMerchantId(event.target.value)}
            disabled={!canEdit}
            placeholder={t("settings.payments.merchantIdPlaceholder")}
          />
        </div>

        <div className="space-y-2">
          <Label>{t("settings.payments.hashKeyLabel")}</Label>
          <Input
            type="password"
            value={hashKey}
            onChange={(event) => setHashKey(event.target.value)}
            disabled={!canEdit}
            placeholder={
              hasExistingAccount
                ? t("settings.payments.maskedPlaceholder")
                : t("settings.payments.hashKeyPlaceholder")
            }
          />
          {hasExistingAccount && hashKeyPreview ? (
            <p className="text-xs text-muted-foreground">
              {t("settings.payments.currentSecretPreview", {
                preview: hashKeyPreview,
              })}
            </p>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label>{t("settings.payments.hashIvLabel")}</Label>
          <Input
            type="password"
            value={hashIv}
            onChange={(event) => setHashIv(event.target.value)}
            disabled={!canEdit}
            placeholder={
              hasExistingAccount
                ? t("settings.payments.maskedPlaceholder")
                : t("settings.payments.hashIvPlaceholder")
            }
          />
          {hasExistingAccount && hashIvPreview ? (
            <p className="text-xs text-muted-foreground">
              {t("settings.payments.currentSecretPreview", {
                preview: hashIvPreview,
              })}
            </p>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          {hasExistingAccount
            ? t("settings.payments.keepSecretHint")
            : t("settings.payments.createSecretHint")}
        </p>

        <Button
          onClick={handleSave}
          disabled={
            !canEdit ||
            upsertStatus === FetchStatus.Loading ||
            gatewayStatus === FetchStatus.Loading
          }
        >
          {upsertStatus === FetchStatus.Loading
            ? t("settings.payments.saving")
            : t("settings.payments.save")}
        </Button>
      </div>
    </section>
  )
}

export default WorkspacePaymentGatewaySection
